import { useEffect } from 'react';
import {
  Text,
  Paper,
  Stack,
  TextInput,
  Button,
  Skeleton,
  Group,
  SimpleGrid,
  Badge,
  Title,
  ThemeIcon,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconBuilding,
  IconUser,
  IconTruckDelivery,
  IconFileInvoice,
  IconBell,
  IconArrowRight,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/PageHeader';
import { useCustomerProfile, useUpdateCustomerProfile } from '@/hooks/usePortalApi';

// ---------------------------------------------------------------------------
// Helper: read-only label / value pair
// ---------------------------------------------------------------------------
function InfoField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text size="sm" fw={500}>
        {value || '--'}
      </Text>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section header with icon
// ---------------------------------------------------------------------------
function SectionHeader({
  icon,
  color,
  title,
  description,
}: {
  icon: React.ReactNode;
  color: string;
  title: string;
  description?: string;
}) {
  return (
    <Group gap="sm" mb="md">
      <ThemeIcon variant="light" size="lg" radius="xl" color={color}>
        {icon}
      </ThemeIcon>
      <div>
        <Title order={4}>{title}</Title>
        {description && (
          <Text size="sm" c="dimmed">
            {description}
          </Text>
        )}
      </div>
    </Group>
  );
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------
export default function Profile() {
  const { data: profile, isLoading } = useCustomerProfile();
  const updateProfile = useUpdateCustomerProfile();
  const navigate = useNavigate();

  // ── Primary Contact form ───────────────────────────────────────────────
  const primaryForm = useForm({
    initialValues: {
      contact_name: '',
      email: '',
      phone: '',
      secondary_phone: '',
      website: '',
      business_hours: '',
    },
  });

  // ── Receiving Contact form ─────────────────────────────────────────────
  const receivingForm = useForm({
    initialValues: {
      receiving_contact_name: '',
      receiving_contact_phone: '',
    },
  });

  // ── AP / Billing Contact form ──────────────────────────────────────────
  const apForm = useForm({
    initialValues: {
      ap_contact_name: '',
      ap_contact_email: '',
      ap_contact_phone: '',
    },
  });

  // ── Hydrate forms when profile loads ───────────────────────────────────
  useEffect(() => {
    if (profile) {
      primaryForm.setValues({
        contact_name: profile.contact_name || '',
        email: profile.email || '',
        phone: profile.phone || '',
        secondary_phone: profile.secondary_phone || '',
        website: profile.website || '',
        business_hours: profile.business_hours || '',
      });
      receivingForm.setValues({
        receiving_contact_name: profile.receiving_contact_name || '',
        receiving_contact_phone: profile.receiving_contact_phone || '',
      });
      apForm.setValues({
        ap_contact_name: profile.ap_contact_name || '',
        ap_contact_email: profile.ap_contact_email || '',
        ap_contact_phone: profile.ap_contact_phone || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  // ── Save helpers ───────────────────────────────────────────────────────
  const handleSave = (values: Record<string, string>) => {
    updateProfile.mutate(values, {
      onSuccess: () => {
        notifications.show({
          title: 'Profile updated',
          message: 'Your changes have been saved',
          color: 'brand',
        });
      },
      onError: () => {
        notifications.show({
          title: 'Error',
          message: 'Failed to update profile',
          color: 'red',
        });
      },
    });
  };

  // ── Loading state ──────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <Stack gap="lg">
        <Skeleton height={30} width={200} />
        <Skeleton height={250} />
        <Skeleton height={300} />
        <Skeleton height={200} />
        <Skeleton height={200} />
        <Skeleton height={120} />
      </Stack>
    );
  }

  // ── Format currency ────────────────────────────────────────────────────
  const formatCurrency = (val: string | undefined | null) => {
    if (!val) return '--';
    const num = parseFloat(val);
    if (isNaN(num)) return val;
    return num.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  };

  // ── Tax status display ────────────────────────────────────────────────
  const taxStatusDisplay = () => {
    if (profile?.tax_exempt) {
      return (
        <Group gap="xs">
          <Badge color="green" variant="light" size="sm">
            Tax Exempt
          </Badge>
          {profile.tax_id && (
            <Text size="xs" c="dimmed">
              {profile.tax_id}
            </Text>
          )}
        </Group>
      );
    }
    return <Text size="sm">Taxable</Text>;
  };

  return (
    <Stack gap="xl">
      <PageHeader
        title="My Profile"
        description="View your account details and manage contact information"
      />

      {/* ── Section 1: Account Information (read-only) ──────────────────── */}
      <Paper p="lg" radius="md" bg="gray.0">
        <SectionHeader
          icon={<IconBuilding size={20} />}
          color="gray"
          title="Account Information"
        />

        <SimpleGrid cols={{ base: 1, xs: 2, md: 3 }} spacing="lg">
          <InfoField label="Account Name" value={profile?.name} />
          <InfoField label="Account Code" value={profile?.code} />
          <InfoField label="Customer Type" value={profile?.customer_type} />
          <InfoField label="Pricing Tier" value={profile?.pricing_tier} />
          <InfoField label="Payment Terms" value={profile?.payment_terms} />
          <InfoField label="Salesperson" value={profile?.salesperson} />
          <InfoField label="Credit Limit" value={formatCurrency(profile?.credit_limit)} />
          <InfoField label="Balance" value={formatCurrency(profile?.balance)} />
          <InfoField label="Tax Status" value={taxStatusDisplay()} />
          <InfoField label="Delivery Route" value={profile?.delivery_route} />
          <InfoField label="Delivery Window" value={profile?.delivery_window} />
        </SimpleGrid>
      </Paper>

      {/* ── Section 2: Primary Contact (editable) ──────────────────────── */}
      <Paper p="lg" radius="md">
        <SectionHeader
          icon={<IconUser size={20} />}
          color="brand"
          title="Primary Contact"
          description="Main point of contact for this account"
        />

        <form onSubmit={primaryForm.onSubmit(handleSave)}>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" maw={700}>
            <TextInput
              label="Contact Name"
              placeholder="Full name"
              {...primaryForm.getInputProps('contact_name')}
            />
            <TextInput
              label="Email"
              placeholder="email@example.com"
              {...primaryForm.getInputProps('email')}
            />
            <TextInput
              label="Phone"
              placeholder="(555) 123-4567"
              {...primaryForm.getInputProps('phone')}
            />
            <TextInput
              label="Secondary Phone"
              placeholder="(555) 123-4567"
              {...primaryForm.getInputProps('secondary_phone')}
            />
            <TextInput
              label="Website"
              placeholder="https://example.com"
              {...primaryForm.getInputProps('website')}
            />
            <TextInput
              label="Business Hours"
              placeholder="e.g. Mon-Fri 8 AM - 5 PM"
              {...primaryForm.getInputProps('business_hours')}
            />
          </SimpleGrid>
          <Group justify="flex-end" mt="md">
            <Button type="submit" loading={updateProfile.isPending}>
              Save Primary Contact
            </Button>
          </Group>
        </form>
      </Paper>

      {/* ── Section 3: Receiving Contact (editable) ────────────────────── */}
      <Paper p="lg" radius="md">
        <SectionHeader
          icon={<IconTruckDelivery size={20} />}
          color="cyan"
          title="Receiving Contact"
          description="Contact person for deliveries and receiving"
        />

        <form onSubmit={receivingForm.onSubmit(handleSave)}>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" maw={700}>
            <TextInput
              label="Receiving Contact Name"
              placeholder="Full name"
              {...receivingForm.getInputProps('receiving_contact_name')}
            />
            <TextInput
              label="Receiving Contact Phone"
              placeholder="(555) 123-4567"
              {...receivingForm.getInputProps('receiving_contact_phone')}
            />
          </SimpleGrid>
          <Group justify="flex-end" mt="md">
            <Button type="submit" loading={updateProfile.isPending}>
              Save Receiving Contact
            </Button>
          </Group>
        </form>
      </Paper>

      {/* ── Section 4: AP / Billing Contact (editable) ─────────────────── */}
      <Paper p="lg" radius="md">
        <SectionHeader
          icon={<IconFileInvoice size={20} />}
          color="slate"
          title="AP / Billing Contact"
          description="Contact person for invoices, payments, and billing inquiries"
        />

        <form onSubmit={apForm.onSubmit(handleSave)}>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md" maw={700}>
            <TextInput
              label="AP Contact Name"
              placeholder="Full name"
              {...apForm.getInputProps('ap_contact_name')}
            />
            <TextInput
              label="AP Contact Email"
              placeholder="ap@example.com"
              {...apForm.getInputProps('ap_contact_email')}
            />
            <TextInput
              label="AP Contact Phone"
              placeholder="(555) 123-4567"
              {...apForm.getInputProps('ap_contact_phone')}
            />
          </SimpleGrid>
          <Group justify="flex-end" mt="md">
            <Button type="submit" loading={updateProfile.isPending}>
              Save AP Contact
            </Button>
          </Group>
        </form>
      </Paper>

      {/* ── Section 5: Notification Contacts ───────────────────────────── */}
      <Paper p="lg" radius="md">
        <SectionHeader
          icon={<IconBell size={20} />}
          color="violet"
          title="Notification Contacts"
        />

        <Text size="sm" c="dimmed" mb="md">
          Manage notification settings including email and SMS preferences
        </Text>

        <Button
          variant="light"
          rightSection={<IconArrowRight size={16} />}
          onClick={() => navigate('/account/preferences')}
        >
          Manage Notification Preferences
        </Button>
      </Paper>
    </Stack>
  );
}
